import Constants from '@/Constants';
import Option from '@/models/Option';
import { ApplicationState } from '@/store';
import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import './style.scss';

interface Handles {
    clearFilterTag: (option?: Option) => void;
}

interface Props {
    label: string;
    options: Option[];
    onSelectOption: (selectedOptions: Option[], option?: Option) => void;
    selectedOptionsKey: Option[];
}

const FilterDropdown: React.ForwardRefRenderFunction<Handles, Props> = (
    { label, options, onSelectOption, selectedOptionsKey }: Props,
    forwardedRef,
) => {
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const [selectedOptions, setSelectedOptions] = useState(selectedOptionsKey || []);

    const handleSelectOption = (option: Option) => {
        const index = selectedOptions.findIndex((opt) => opt.key === option.key);
        const newSelectedOptions = [...selectedOptions];
        if (index === -1) {
            newSelectedOptions.push(option);
        } else {
            newSelectedOptions.splice(index, 1);
        }
        setSelectedOptions(newSelectedOptions);

        if (onSelectOption) {
            onSelectOption(newSelectedOptions, option);
        }
    };

    const clearFilterTag = (option?: Option) => {
        if (option) {
            handleSelectOption(option);
        } else {
            setSelectedOptions([]);
            if (onSelectOption) {
                onSelectOption([], undefined);
            }
        }
    };

    useImperativeHandle(forwardedRef, () => ({
        clearFilterTag: (option) => clearFilterTag(option),
    }));

    return (
        <div className="filter-dropdown-container">
            <span className="filter-text">{strings.FILTER_BY}:</span>
            <div className="dropdown">
                <button className={`btn btn-filter ${selectedOptions.length > 0 ? 'is-active' : ''}`} data-toggle="dropdown" type="button">
                    {label}
                </button>
                <div className="dropdown-menu dropdown-menu-right drop-three">
                    <div className="triangle-container t-three">
                        <div className="dropdown-triangle" />
                        <div className="dropdown-triangle-border" />
                    </div>
                    {options.map((option) => {
                        const isSelected: boolean = !!selectedOptions.find((selectedOption: Option) => selectedOption.key === option.key);

                        return (
                            <div
                                className="dropdown-content"
                                key={option.key}
                                onClick={() => handleSelectOption(option)}
                                onKeyDown={() => {}}
                                role="button"
                                tabIndex={0}
                            >
                                <i className={`material-icons va-t m-right--xxs ${isSelected ? 'is-active' : ''}`}>
                                    {isSelected ? Constants.icons.CHECK_BOX : Constants.icons.CHECK_BOX_OUTLINE_BLANK}
                                </i>
                                <span>{option.value}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default memo(forwardRef(FilterDropdown));
